import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
export interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-[#0f1d35] ${className}`} data-id="element-338">
      <div className="container mx-auto px-4" data-id="element-339">
        {title && <h2 className="font-bold text-center mb-12 text-white text-[16px] md:text-2xl" data-id="element-340">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-341">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-[#0a1428]/70 p-6 rounded-2xl shadow-lg border border-[#1e3a5f] flex flex-col h-full" data-id="element-342">
              <div className="flex items-center gap-1 mb-4" data-id="element-343">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-slate-300 fill-slate-300" data-id="element-344" />)}
              </div>

              <p className="text-slate-300 italic mb-6 flex-grow" data-id="element-345">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-[#1e3a5f]/50" data-id="element-346">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-347">
                  {t.initials}
                </div>
                <div data-id="element-348">
                  <p className="font-bold text-white text-sm" data-id="element-349">{t.name}</p>
                  <p className="text-xs text-slate-400" data-id="element-350">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-slate-300/90 mt-0.5" data-id="element-351">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials: Testimonial[] = [{
  initials: 'MR',
  name: 'María R.',
  location: 'Docente de Geografía · Primaria · Mendoza',
  text: 'Llevaba años buscando material de meteorología adaptado a Argentina. Abrí el Tomo 1 y en una tarde tenía toda la planificación de la unidad lista. Mis alumnos por fin entienden el Zonda y el Pampero porque los ejemplos son de acá.',
  color: 'bg-blue-700'
}, {
  initials: 'JC',
  name: 'Julián C.',
  location: 'Docente de Ciencias Naturales · Secundaria · Córdoba',
  text: 'El Tomo 4 de Instrumentos me dio todo lo que necesitaba para armar la estación meteorológica escolar. Antes buscaba en cinco sitios distintos y nunca encontraba nada contextualizado. Esto lo abrí, lo imprimí y lo usé al día siguiente.',
  color: 'bg-sky-800'
}, {
  initials: 'SP',
  name: 'Sofía P.',
  location: 'Maestra de grado · Buenos Aires',
  text: 'Las 100 Actividades del Bono 1 las empecé a usar en la primera semana. Tengo actividades para primaria, para secundaria, para feria de ciencias y para trabajo interdisciplinario. No tuve que inventar nada. Solo elegir y enseñar.',
  color: 'bg-blue-900'
}];
export const moreTestimonials: Testimonial[] = [{
  initials: 'PM',
  name: 'Pablo M.',
  location: '🏙️ Buenos Aires',
  text: 'Dos años enseñando ciencias naturales sin encontrar material de meteorología que hable del Pampero o la Sudestada. Con el Megapack en una tarde tenía actividades concretas con fenómenos que mis alumnos de Buenos Aires viven en primera persona.',
  color: 'bg-blue-800',
  pack: 'Megapack Meteorología'
}, {
  initials: 'RL',
  name: 'Rosa L.',
  location: '🏙️ Santa Fe',
  text: 'El Tomo 6 de Cambio Climático me dio todo el marco legal para cumplir con la EAI. Antes le tenía miedo a ese tema porque no sabía cómo abordarlo sin material sólido. Ahora tengo el proyecto aprobado por la dirección.',
  color: 'bg-slate-700',
  pack: 'Megapack Meteorología'
}, {
  initials: 'GF',
  name: 'Gabriela F.',
  location: '🏙️ Tucumán',
  text: 'Con el Bono de Experimentos armé la feria de ciencias en una semana. Elegí 8 experimentos, los imprimí, repartí roles y mis alumnos presentaron con seguridad. Nunca había tenido una feria tan organizada.',
  color: 'bg-cyan-800',
  pack: 'Megapack + Nivel Maestro'
}, {
  initials: 'AM',
  name: 'Andrés M.',
  location: '🏙️ Mendoza',
  text: 'Cerré la planificación anual de meteorología en dos horas usando el Nivel Maestro. Antes me llevaba semanas y siempre quedaba algo sin articular entre los temas. Ahora está todo secuenciado y con conexión curricular clara.',
  color: 'bg-blue-700',
  pack: 'Megapack + Nivel Maestro'
}, {
  initials: 'SV',
  name: 'Silvia V.',
  location: '🏙️ Córdoba',
  text: 'Las 64 Fichas Imprimibles las organicé por tema y las tengo en una carpeta lista para sacar cuando la necesito. Mis alumnos las pegan en la carpeta y tienen material concreto para estudiar. No tuve que dibujar ni una sola vez en el pizarrón.',
  color: 'bg-sky-900',
  pack: 'Megapack Meteorología'
}];